import React, { useState, useEffect } from 'react';
import { db, storage } from '../firebaseConfig';
import { collection, getDocs, doc, updateDoc, getDoc } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { updateProfile } from 'firebase/auth';
import { showToast } from '../components/Toast';
import '../css/gestion.css';

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const querySnapshot = await getDocs(collection(db, 'users'));
      const usersData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUsers(usersData);
      showToast(`✅ ${usersData.length} usuarios cargados`, 'success', true);
    } catch (error) {
      console.error('Error al cargar usuarios:', error);
      showToast('❌ Error al cargar usuarios', 'error', true);
    } finally {
      setLoading(false);
    }
  };

  const updateUserRole = async (userId, newRole) => {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        role: newRole
      });
      
      setUsers(users.map(user => 
        user.id === userId ? { ...user, role: newRole } : user
      ));
      
      showToast(`✅ Rol actualizado a ${getRoleLabel(newRole)}`, 'success', true);
    } catch (error) {
      console.error('Error al actualizar rol:', error);
      showToast('❌ Error al actualizar rol', 'error', true);
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'superadmin':
        return '👑 Super Admin';
      case 'admin':
        return '⭐ Admin';
      case 'client':
      default:
        return '🎵 Cliente';
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'superadmin':
        return 'role-superadmin';
      case 'admin':
        return 'role-admin';
      case 'client':
      default:
        return 'role-client';
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.displayName?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterRole === 'all' || user.role === filterRole;
    
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="user-management-container">
      <div className="management-header">
        <h1>👥 Gestión de Usuarios</h1>
        <p>Administra los roles y permisos de los usuarios</p>
      </div>

      <div className="management-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="🔍 Buscar por email o nombre..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-box">
          <label>Filtrar por rol:</label>
          <select 
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className="filter-select"
          >
            <option value="all">📊 Todos</option>
            <option value="client">🎵 Cliente</option>
            <option value="admin">⭐ Admin</option>
            <option value="superadmin">👑 Super Admin</option>
          </select>
        </div>

        <div className="user-stats">
          <span className="stat">Total: <strong>{users.length}</strong></span>
          <span className="stat">Mostrados: <strong>{filteredUsers.length}</strong></span>
        </div>
      </div>

      {loading ? (
        <div className="loading">
          <div className="spinner"></div>
          <p>Cargando usuarios...</p>
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="empty-state">
          <h2>😕 No hay usuarios</h2>
          <p>No se encontraron usuarios con esos criterios</p>
        </div>
      ) : (
        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>Usuario</th>
                <th>Email</th>
                <th>Rol Actual</th>
                <th>Acciones</th>
                <th>Se unió</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr key={user.id} className={`user-row ${getRoleColor(user.role)}`}>
                  <td className="user-name">
                    <div className="user-avatar">
                      {user.photoURL ? (
                        <img 
                          src={user.photoURL} 
                          alt={user.displayName}
                          onError={async (e) => {
                            try {
                              const userRef = doc(db, 'users', user.id);
                              const userDoc = await getDoc(userRef);
                              if (userDoc.exists() && userDoc.data().photoPath) {
                                const newURL = await getDownloadURL(ref(storage, userDoc.data().photoPath));
                                e.target.src = newURL;
                              }
                            } catch (err) {
                              e.target.style.display = 'none';
                            }
                          }}
                        />
                      ) : (
                        <div className="avatar-placeholder">
                          {user.displayName?.charAt(0).toUpperCase() || user.email?.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div className="user-info">
                      <span className="user-display-name">{user.displayName || 'Sin nombre'}</span>
                      <span className="user-id">ID: {user.id.substring(0, 8)}...</span>
                    </div>
                  </td>
                  <td className="user-email">
                    {user.email}
                  </td>
                  <td>
                    <span className={`role-badge ${getRoleColor(user.role)}`}>
                      {getRoleLabel(user.role)}
                    </span>
                  </td>
                  <td>
                    <div className="role-buttons">
                      <button
                        onClick={() => updateUserRole(user.id, 'client')}
                        className={`role-btn btn-client ${user.role === 'client' ? 'active' : ''}`}
                        title="Hacer Cliente"
                      >
                        👤 Cliente
                      </button>
                      <button
                        onClick={() => updateUserRole(user.id, 'admin')}
                        className={`role-btn btn-admin ${user.role === 'admin' ? 'active' : ''}`}
                        title="Hacer Admin"
                      >
                        ⭐ Admin
                      </button>
                      <button
                        onClick={() => updateUserRole(user.id, 'superadmin')}
                        className={`role-btn btn-superadmin ${user.role === 'superadmin' ? 'active' : ''}`}
                        title="Hacer Super Admin"
                      >
                        👑 Super
                      </button>
                    </div>
                  </td>
                  <td className="user-date">
                    {user.createdAt?.toDate?.()?.toLocaleDateString('es-ES') || 'N/A'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default UserManagement;